import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Container,
  Typography,
  Button,
  Paper,
  IconButton,
  Grid,
  useMediaQuery,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
} from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import MenuIcon from '@mui/icons-material/Menu';
import { useInView } from 'react-intersection-observer';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import PrivacyPolicy from './components/PrivacyPolicy';
import HowItWorks from './components/HowItWorks';
import LikeButton from './components/LikeButton';
import FeedbackToast from './components/FeedbackToast';
import { subscriptionService } from './services/subscriptionService';
import { SubscriptionStats } from './data/interface';

const getBackgroundImage = () => {
  const index = Math.floor(Math.random() * 7) + 1;
  return require(`./assets/background-image-${index}.avif`);
};

const backgroundImage = getBackgroundImage();

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#ff4081',
    },
    secondary: {
      main: '#00e5ff',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const MotionBox = motion(Box);

function App() {
  const [isThumbsUpAnimating, setIsThumbsUpAnimating] = useState(false);
  const [shootingTexts, setShootingTexts] = useState<Array<{ id: number; text: string; angle: number }>>([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [likes, setLikes] = useState(0);
  const [djCount, setDjCount] = useState(0);

  const fetchStats = async () => {
    const stats = await subscriptionService.getStats();
    setLikes(stats.likes);
    setDjCount(stats.djCount);
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);

  const { ref: heroRef, inView: heroInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { ref: whatIsRef, inView: whatIsInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });


  const handleThumbsUp = async () => {
    try {
      setIsThumbsUpAnimating(true);
      // Get IP address using a public API
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      const ip = data.ip;

      await subscriptionService.addLike(ip);
      setLikes(prev => prev + 1);
      
      // Add new shooting text
      const texts = ['You made us work faster!', 'Awesome!', 'Grazie!', 'Boom!'];
      const newText = {
        id: Date.now(),
        text: texts[Math.floor(Math.random() * texts.length)],
        angle: Math.random() * 60 - 30
      };
      setShootingTexts(prev => [...prev, newText]);

      // Remove the text after animation completes
      setTimeout(() => {
        setShootingTexts(prev => prev.filter(text => text.id !== newText.id));
      }, 3500);

      setTimeout(() => setIsThumbsUpAnimating(false), 1000);
    } catch (error) {
      if (error instanceof Error && error.message === 'IP has already liked') {
        // Show a message that they've already liked
        const newText = {
          id: Date.now(),
          text: 'You already liked this!',
          angle: Math.random() * 60 - 30
        };
        setShootingTexts(prev => [...prev, newText]);
        setTimeout(() => {
          setShootingTexts(prev => prev.filter(text => text.id !== newText.id));
        }, 3500);
      } else {
        console.error('Error adding like:', error);
      }
      setIsThumbsUpAnimating(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Router>
      <Routes>
        {/* <Route path="/pricing" element={<Pricing />} /> */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/" element={
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
              {/* Glassy Navigation Bar */}
              <AppBar
                position="fixed"
                sx={{
                  background: 'linear-gradient(135deg, rgba(255, 64, 129, 0.1) 0%, rgba(0, 229, 255, 0.1) 100%)',
                  backdropFilter: 'blur(20px)',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: 'none',
                  zIndex: 1001,
                }}
              >
                <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, sm: 3 } }}>
                  {/* Logo */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      component="img"
                      src={require('./assets/artistplaylogo.png')}
                      alt="ArtistPlay Logo"
                      sx={{
                        height: { xs: '32px', sm: '40px' },
                        width: 'auto',
                        objectFit: 'contain'
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
                        backgroundClip: 'text',
                        textFillColor: 'transparent',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      }}
                    >
                      ArtistPlay
                    </Typography>
                  </Box>

                  {/* Desktop Navigation */}
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
                    <Button
                      component={Link}
                      to="/how-it-works"
                      variant="text"
                      sx={{
                        color: 'text.primary',
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      How this works
                    </Button>
                    <LikeButton
                      onClick={handleThumbsUp}
                      isAnimating={isThumbsUpAnimating}
                      shootingTexts={shootingTexts}
                      size="medium"
                    />
                    <Button
                      component="a"
                      href="https://app.artistplay.io/"
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(135deg, #ff4081 0%, #00e5ff 100%)',
                        color: 'white',
                        textTransform: 'none',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #ff4081 20%, #00e5ff 100%)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      Sign up
                    </Button>
                  </Box>

                  {/* Mobile Menu Button */}
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ display: { md: 'none' }, color: 'text.primary' }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Toolbar>
              </AppBar>

              {/* Mobile Drawer */}
              <Drawer
                variant="temporary"
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                  keepMounted: true,
                }}
                sx={{
                  display: { xs: 'block', md: 'none' },
                  '& .MuiDrawer-paper': {
                    width: 240,
                    background: 'linear-gradient(135deg, rgba(255, 64, 129, 0.1) 0%, rgba(0, 229, 255, 0.1) 100%)',
                    backdropFilter: 'blur(20px)',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Box
                      component="img"
                      src={require('./assets/artistplaylogo.png')}
                      alt="ArtistPlay Logo"
                      sx={{
                        height: '32px',
                        width: 'auto',
                        objectFit: 'contain'
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
                        backgroundClip: 'text',
                        textFillColor: 'transparent',
                        fontSize: '1.1rem',
                      }}
                    >
                      ArtistPlay
                    </Typography>
                  </Box>
                  <List>
                                      <ListItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 1 }}>
                        <LikeButton
                          onClick={() => {
                            handleThumbsUp();
                            handleDrawerToggle();
                          }}
                          isAnimating={isThumbsUpAnimating}
                          shootingTexts={shootingTexts}
                          size="medium"
                        />
                      </Box>
                    </ListItem>
                    <ListItem>
                      <Button
                        component={Link}
                        to="/how-it-works"
                        variant="text"
                        fullWidth
                        onClick={handleDrawerToggle}
                        sx={{
                          color: 'text.primary',
                          textTransform: 'none',
                          fontSize: '1rem',
                          fontWeight: 500,
                          justifyContent: 'flex-start',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        How this works
                      </Button>
                    </ListItem>
                    <ListItem>
                      <Button
                        component="a"
                        href="https://app.artistplay.io/"
                        variant="contained"
                        fullWidth
                        onClick={handleDrawerToggle}
                        sx={{
                          background: 'linear-gradient(135deg, #ff4081 0%, #00e5ff 100%)',
                          color: 'white',
                          textTransform: 'none',
                          py: 1.5,
                          borderRadius: 2,
                          fontSize: '1rem',
                          fontWeight: 600,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #ff4081 20%, #00e5ff 100%)',
                          },
                        }}
                      >
                        Sign up
                      </Button>
                    </ListItem>
                  </List>
                </Box>
              </Drawer>

              {/* Toolbar spacer to prevent content from hiding behind fixed navbar */}
              <Toolbar />

              {/* Hero Section */}
              <Box
                ref={heroRef}
                sx={{
                  height: '100vh',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Background Image with Parallax */}
                <MotionBox
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                  }}
                  style={{ y }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url(${backgroundImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(45deg, rgba(18, 18, 18, 0.9) 0%, rgba(18, 18, 18, 0.7) 100%)',
                      },
                    }}
                  />
                </MotionBox>

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                  <Grid container spacing={4} alignItems="center">
                    {/* Left side */}
                    <Grid size={{ xs: 12, md: 6 }} component="div">
                      <MotionBox
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={heroInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2
                          }}
                        >
                          <Box
                            component="img"
                            src={require('./assets/artistplaylogo.png')}
                            alt="ArtistPlay Logo"
                            sx={{
                              height: { xs: '60px', md: '80px' },
                              width: 'auto',
                              objectFit: 'contain'
                            }}
                          />
                          <Typography
                            variant="h1"
                            sx={{
                              fontSize: { xs: '3rem', md: '4.5rem' },
                              fontWeight: 700,
                              background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
                              backgroundClip: 'text',
                              textFillColor: 'transparent',
                              lineHeight: 1.1,
                            }}
                          >
                            ArtistPlay
                          </Typography>
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{
                            color: 'text.secondary',
                            maxWidth: '600px',
                            lineHeight: 1.4,
                          }}
                        >
                          Designed for DJs — Connect with your audience and earn tips while you play
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.5, sm: 1 },
                            px: { xs: 1.25, sm: 2 },
                            py: { xs: 0.5, sm: 0.75 },
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%)',
                            border: '1px solid rgba(76, 175, 80, 0.3)',
                            backdropFilter: 'blur(10px)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                            <Typography
                              sx={{
                                fontSize: { xs: '0.75rem', sm: '0.95rem' },
                                fontWeight: 600,
                                color: '#4CAF50',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                whiteSpace: 'nowrap',
                                lineHeight: 1,
                              }}
                            >
                              ✓ <strong>Free for DJs</strong>
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              opacity: 0.8,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              fontWeight: 400,
                            }}
                          >
                            No setup fees • No monthly costs
                          </Typography>
                        </Box>
                      </MotionBox>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }} component="div">

                    {/* CTA Card */}
                      <Box sx={{ position: 'relative', zIndex: 2 }}>
                      <Paper
                        sx={{
                          p: 4,
                          borderRadius: 3,
                          backdropFilter: 'blur(20px)',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12 }} component="div">
                            <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
                              Get started now
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 2,
                                color: 'text.secondary',
                                opacity: 0.9,
                                fontSize: '0.95rem',
                                fontWeight: 400,
                              }}
                            >
                              Create your free DJ profile and start taking requests and tips.
                            </Typography>
                            <Button
                              component="a"
                              href="https://app.artistplay.io/"
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="contained"
                              sx={{
                                px: 3,
                                py: 1.25,
                                borderRadius: 2,
                                background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
                                color: 'white',
                                fontWeight: 700,
                                textTransform: 'none',
                                '&:hover': {
                                  background: 'linear-gradient(45deg, #ff4081 40%, #00e5ff 100%)',
                                  transform: 'translateY(-1px)'
                                },
                              }}
                            >
                              Sign up — it’s free
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                      </Box>
                    </Grid>
                  </Grid>
                </Container>
              </Box>

              {/* What is ArtistPlay Section */}
              <Box
                ref={whatIsRef}
                sx={{
                  py: 12,
                  bgcolor: 'background.default',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 64, 129, 0.5), transparent)',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle at center, rgba(255, 64, 129, 0.1) 0%, transparent 70%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Container maxWidth="lg">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <MotionBox
                      initial={{ opacity: 0, y: 20 }}
                      animate={whatIsInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.6 }}
                    >
                      <Typography
                        variant="h2"
                        align="center"
                        sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
                          backgroundClip: 'text',
                          textFillColor: 'transparent',
                          mb: 2,
                          fontSize: { xs: '2rem', sm: '3rem', md: '3.75rem' },
                        }}
                      >
                        What is ArtistPlay?
                      </Typography>
                      <Typography
                        variant="h6"
                        align="center"
                        sx={{ color: 'text.secondary', maxWidth: '800px', mx: 'auto' }}
                      >
                        A revolutionary platform that transforms how DJs connect with their audience
                      </Typography>
                    </MotionBox>
                    <MotionBox
                      initial={{ opacity: 0, y: 20 }}
                      animate={whatIsInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.6, delay: 0.2 }}
                      sx={{
                        maxWidth: '900px',
                        mx: 'auto',
                        mb: 6,
                      }}
                    >
                      <Paper
                        sx={{
                          p: 4,
                          background: 'linear-gradient(135deg, rgba(255, 64, 129, 0.05) 0%, rgba(0, 229, 255, 0.05) 100%)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 3,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                              }}
                            >
                              The Challenge
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(255, 64, 129, 0.3), rgba(0, 229, 255, 0.3))' }} />
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '1.1rem',
                              lineHeight: 1.7,
                            }}
                          >
                            You’re in the zone, reading the room, keeping the dance floor alive — then someone leans in, shouting a song request mid-set. You nod, try to stay cool, but it breaks your flow. It’s distracting, it throws off your timing, and sometimes… the request just doesn’t fit.
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                              }}
                            >
                              The Solution
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(255, 64, 129, 0.3), rgba(0, 229, 255, 0.3))' }} />
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'text.primary',
                              fontSize: '1.1rem',
                              lineHeight: 1.7,
                              fontWeight: 500,
                              background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
                              backgroundClip: 'text',
                              textFillColor: 'transparent',
                            }}
                          >
                            Give your crowd a voice without losing control.  You want to keep them engaged and sometimes, you actually want to know what they’re into.
                            Because guessing the vibe is one thing, but... seeing what they really want? That’s powerful.
                          </Typography>
                        </Box>
                      </Paper>
                    </MotionBox>
                  </Box>
                </Container>
              </Box>

              {/* Features Section */}
              <Box
                sx={{
                  py: 12,
                  bgcolor: 'background.paper',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(0, 229, 255, 0.5), transparent)',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle at center, rgba(0, 229, 255, 0.1) 0%, transparent 70%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Container maxWidth="lg">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <MotionBox
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      viewport={{ once: true }}
                    >
                      <Typography
                        variant="h2"
                        align="center"
                        sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(45deg, #00e5ff 30%, #ff4081 90%)',
                          backgroundClip: 'text',
                          textFillColor: 'transparent',
                          mb: 2,
                          fontSize: { xs: '2rem', sm: '3rem', md: '3.75rem' },
                        }}
                      >
                        Why ArtistPlay?
                      </Typography>
                      <Typography
                        variant="h6"
                        align="center"
                        sx={{ color: 'text.secondary', maxWidth: '800px', mx: 'auto' }}
                      >
                        Get your audience to be a part of the show
                      </Typography>
                    </MotionBox>

                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      gap: 4,
                      position: 'relative',
                    }}>
                      {[
                        {
                          title: 'Support DJs Directly',
                          description: "Every song request is more than a suggestion — it's a chance to tip and support the talent behind the decks. Turn audience interaction into meaningful earnings, perfect for indie and event DJs.",
                          gradient: 'linear-gradient(135deg, rgba(0, 229, 255, 0.1) 0%, rgba(255, 64, 129, 0.1) 100%)',
                          icon: '💸',
                        },
                        {
                          title: 'Interactive Music Moments',
                          description: 'Turn requests into real connections. Guests suggest songs during dedicated DJ interaction windows — no interruptions, just curated moments of engagement.',
                          gradient: 'linear-gradient(135deg, rgba(255, 64, 129, 0.1) 0%, rgba(0, 229, 255, 0.1) 100%)',
                          icon: '🎧',
                        },
                        {
                          title: 'Shoutouts That Hit the Beat',
                          description: 'Dedications, celebrations, inside jokes — every request can come with a personal message, making the night unforgettable for your guests.',
                          gradient: 'linear-gradient(135deg, rgba(255, 64, 129, 0.1) 0%, rgba(0, 229, 255, 0.1) 100%)',
                          icon: '📣',
                        },
                      ].map((feature, index) => (
                        <Box key={index} sx={{ flex: 1 }}>
                          <MotionBox
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.2 }}
                            viewport={{ once: true }}
                            whileHover={{ scale: 1.02 }}
                            sx={{
                              height: '100%',
                              perspective: '1000px',
                            }}
                          >
                            <Paper
                              sx={{
                                p: 4,
                                height: '100%',
                                background: feature.gradient,
                                backdropFilter: 'blur(20px)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                gap: 3,
                                transition: 'all 0.3s ease-in-out',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                '&:hover': {
                                  transform: 'translateY(-8px)',
                                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                                  '& .icon-wrapper': {
                                    transform: 'scale(1.1) rotate(5deg)',
                                  },
                                },
                              }}
                            >
                              <MotionBox
                                className="icon-wrapper"
                                sx={{
                                  p: 2,
                                  borderRadius: '50%',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'transform 0.3s ease-in-out',
                                }}
                              >
                                {feature.icon}
                              </MotionBox>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 600,
                                  background: 'linear-gradient(45deg, #00e5ff 30%, #ff4081 90%)',
                                  backgroundClip: 'text',
                                  textFillColor: 'transparent',
                                }}
                              >
                                {feature.title}
                              </Typography>
                              <Typography
                                color="text.secondary"
                                sx={{
                                  lineHeight: 1.6,
                                  fontSize: '1.1rem',
                                }}
                              >
                                {feature.description}
                              </Typography>
                            </Paper>
                          </MotionBox>
                        </Box>
                      ))}
                    </Box>


                    {/* Scattered Cards */}
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
                      gap: { xs: 2, md: 4 },
                      position: 'relative',
                      width: '100%',
                      maxWidth: '1200px',
                      mx: 'auto',
                      px: { xs: 2, md: 4 },
                      py: { xs: 4, md: 6 },
                    }}>
                      {[
                        {
                          avatar: '👩',
                          name: 'Lisa',
                          song: 'Blinding Lights',
                          tip: '$5',
                          gridColumn: { xs: '1', md: '3' },
                          gridRow: { xs: '1', md: '1' },
                          rotation: -5,
                        },
                        {
                          avatar: '👨',
                          name: 'Srikanth',
                          song: 'A.R. Rahman Hit',
                          tip: '$20',
                          gridColumn: { xs: '2', md: '2' },
                          gridRow: { xs: '1', md: '2' },
                          rotation: 8,
                        },
                        {
                          avatar: '👧',
                          name: 'Sam',
                          song: 'Return of the Mack',
                          tip: '$7',
                          gridColumn: { xs: '1', md: '4' },
                          gridRow: { xs: '2', md: '2' },
                          rotation: -12,
                        },
                        ...(
                          isMobile ? [
                            {
                              avatar: '👦',
                              name: 'Mike',
                              song: 'Chamak Challo (Remix)',
                              tip: '$15',
                              gridColumn: { xs: '2', sm: '2', md: '1' },
                              gridRow: { xs: '2', sm: '3', md: '3' },
                              rotation: 6,
                            }
                          ] : []
                        ),
                        {
                          avatar: '👩',
                          name: 'Druv',
                          song: 'Drake - Nokia',
                          tip: '$6',
                          gridColumn: { xs: '1 / span 2', sm: '2 / span 2', md: '3' },
                          gridRow: { xs: '3', sm: '3', md: '3' },
                          rotation: -8,
                        },
                      ].map((request, index) => (
                        <MotionBox
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          whileHover={{ rotate: request.rotation + 2 }}
                          animate={{
                            y: [0, -3, -5, -3, 0],
                            rotate: [request.rotation, request.rotation + 0.5, request.rotation + 1, request.rotation + 0.5, request.rotation],
                            opacity: 1
                          }}
                          transition={{
                            y: { duration: 5, repeat: Infinity, delay: index * 0.5, ease: [0.4, 0, 0.2, 1] },
                            rotate: { duration: 5, repeat: Infinity, delay: index * 0.5, ease: [0.4, 0, 0.2, 1] },
                            opacity: { duration: 0.5, delay: index * 0.2 },
                            scale: { type: 'spring', stiffness: 80, damping: 18 }
                          }}
                          sx={{
                            gridColumn: request.gridColumn,
                            gridRow: request.gridRow,
                            width: '100%',
                            maxWidth: { xs: '120px', sm: '140px', md: '200px' },
                            mx: 'auto',
                            transform: `rotate(${request.rotation}deg)`,
                          }}
                        >
                          <Paper
                            sx={{
                              p: { xs: 1, sm: 1.5, md: 2 },
                              background: 'linear-gradient(135deg, rgba(255, 64, 129, 0.1) 0%, rgba(0, 229, 255, 0.1) 100%)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: 2,
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                              width: '100%',
                              transition: 'all 0.3s ease-in-out',
                              '&:hover': {
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                border: '1px solid rgba(255, 64, 129, 0.3)',
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Typography
                                sx={{
                                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                                  lineHeight: 1,
                                }}
                              >
                                {request.avatar}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: 'text.primary',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                }}
                              >
                                {request.name}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                mb: 0.5,
                                fontWeight: 500,
                                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                              }}
                            >
                              <span role="img" aria-label="music">🎵</span> {request.song}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                              }}
                            >
                              <Typography variant="body2">💸</Typography>
                              <Typography variant="body2">{request.tip}</Typography>
                            </Box>
                          </Paper>
                        </MotionBox>
                      ))}
                    </Box>
                  </Box>
                </Container>
              </Box>


              {/* FAQ Section */}
              <Box
                sx={{
                  py: 8,
                  bgcolor: 'background.paper',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(0, 229, 255, 0.5), transparent)',
                  },
                }}
              >
                <Container maxWidth="md">
                  <Typography
                    variant="h4"
                    align="center"
                    sx={{
                      fontWeight: 600,
                      mb: 6,
                      background: 'linear-gradient(45deg, #00e5ff 30%, #ff4081 90%)',
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                    }}
                  >
                    Frequently Asked Questions
                  </Typography>
                  <Grid container spacing={4}>
                    {[
                      {
                        question: 'Do I lose control over the music if guests can request songs?',
                        answer: 'Not at all. You choose when to accept requests, and which ones to play. You stay in full control of the set — guests simply get a way to share what they\'d love to hear, without interrupting you.',
                      },
                      {
                        question: 'What if someone requests a song that doesn\'t fit the vibe?',
                        answer: 'You can reject any request or curate your own accepted genres in advance. It\'s your set — the app just gives you a smarter, smoother way to hear from the crowd.',
                      },
                      {
                        question: 'Will this make people stare at their phones all night?',
                        answer: 'Not likely. The experience is quick and focused. Guests scan a QR code, submit a request or tip, and return to the moment. It\'s designed to enhance connection, not replace it.',
                      },
                      {
                        question: 'Is tipping required? Won\'t that feel pushy?',
                        answer: 'Tipping is 100% optional. Think of it like street performance appreciation — for those who want to support the DJ. You can turn this feature off at any time.',
                      },
                      {
                        question: 'Can I try it before using it live?',
                        answer: 'Yes! Join the waitlist for early access and test the platform in a demo mode before bringing it to your next event.',
                      },
                      {
                        question: 'Why not just take song requests the usual way?',
                        answer: 'Because this is smoother, more professional, and built for today\'s events. No yelling over the music, no forgotten requests, and a better experience for everyone — including you.',
                      },
                      {
                        question: 'Do you charge a fee for tips?',
                        answer: 'We charge a 15% fee on tips (3% payment processing, 12% platform fee). No other charges.',
                      }
                    ].map((faq, index) => (
                      <Grid size={{ xs: 12, md: 6 }} key={index}>
                        <Paper
                          sx={{
                            p: 3,
                            height: '100%',
                            background: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 1,
                              color: 'primary.main',
                              fontWeight: 600,
                            }}
                          >
                            {faq.question}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {faq.answer}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Container>
              </Box>

              {/* Thumbs Up Section */}
              <Box
                sx={{
                  py: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100px',
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 64, 129, 0.5), transparent)',
                  },
                }}
              >

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                    }}
                  >
                    Show some love
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LikeButton
                      onClick={handleThumbsUp}
                      isAnimating={isThumbsUpAnimating}
                      shootingTexts={shootingTexts}
                      size="large"
                      showFeedback={true}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 500,
                      }}
                    >
                      {likes.toLocaleString()} people love it
                    </Typography>
                  </Box>
                </Box>

        
              </Box>

              {/* Footer */}
              <Box
                component="footer"
                sx={{
                  mt: 6,
                  py: 4,
                  bgcolor: 'background.default',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    © {new Date().getFullYear()} ArtistPlay
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Typography variant="body2">
                      <Link to="/how-it-works" style={{ color: 'inherit', textDecoration: 'none' }}>
                        How It Works
                      </Link>
                    </Typography>
                    <Typography variant="body2">
                      <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>
                        Privacy Policy
                      </Link>
                    </Typography>
                  </Box>
                </Container>
              </Box>
            </Box>
            
            {/* Global Feedback Toast */}
            {shootingTexts.length > 0 && (
              <FeedbackToast
                message={shootingTexts[shootingTexts.length - 1].text}
                isVisible={true}
              />
            )}
          </ThemeProvider>
        } />
      </Routes>
    </Router>
  );
}

export default App;
